import { useEffect, useState } from 'react';

import { TapPaymentWidget } from '@/components/atomic/atoms';
import { Button, CloseIcon, Text } from '@/components/atomic/nuclei';
import { useUIContext } from '@/components/context/uiContext/ManagedUiContext';
import config from '@/config';
import { DEVICE_SURFACE } from '@/config/common';
import { GetUiDeviceSurface } from '@/helpers/screenResolutionHelper';
import { parseFormatPriceValueFromAPI } from '@/lib/numberStringUtils';

const ActionButton = ({
  isRequestCallBackApplicable,
  isValid,
  onClickActionButton
}) => (
  <Button
    {...{
      children: isRequestCallBackApplicable
        ? 'Request For Callback'
        : 'Proceed To Payment',
      className:
        'bg-brand-gradient h-12.5 py-4 rounded-md text-white text-base font-medium leading-5 w-full md:max-w-55 md:mt-8 ml-auto md:mb-0',
      disabled: !(isRequestCallBackApplicable || isValid),
      onClick: onClickActionButton,
      type: 'submit'
    }}
  />
);

const CheckoutActionButtonWeb = ({
  handleEventInfoSubmit,
  handleReqCallBackClick,
  hostEmail,
  hostMobile,
  hostName,
  isRequestCallBackApplicable,
  isValid,
  orderTotal,
  renderTapPaymentWidget,
  setLoading,
  setShowToast,
  setTapSanitizedToken,
  setRenderTapPaymentWidget
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // TODO: HP - try to apply class using DOM contex provider
  useEffect(() => {
    const addOrRemove = isModalOpen ? 'add' : 'remove';
    document.body.classList[`${addOrRemove}`]('overflow-hidden');
  }, [isModalOpen]);

  return (
    <div className='flex w-full justify-end relative'>
      {renderTapPaymentWidget && isModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
          <div className='relative rounded-2xl bg-brand-gradient w-full max-w-md p-6 text-white shadow-lg'>
            <div className='flex justify-between'>
              <Text
                {...{
                  className: 'text-base font-medium mb-2',
                  content: `Paying AED ${parseFormatPriceValueFromAPI(
                    orderTotal
                  )}`
                }}
              />
              <CloseIcon
                icon='close-with-border-white.svg'
                onClick={() => {
                  setIsModalOpen(false);
                  setRenderTapPaymentWidget(false);
                }}
              />
            </div>
            <TapPaymentWidget
              {...{
                hostEmail,
                hostMobile,
                hostName,
                onClickPayAmount: handleEventInfoSubmit,
                setLoading,
                setShowToast,
                setTapSanitizedToken,
                textColorClass: 'text-brand',
                transactionAmountInUpperDenomination:
                  parseFormatPriceValueFromAPI(orderTotal)
              }}
            />
          </div>
        </div>
      )}
      <ActionButton
        {...{
          isRequestCallBackApplicable,
          isValid,
          onClickActionButton: isRequestCallBackApplicable
            ? handleReqCallBackClick
            : () => {
                if (!config.isRenderTapPaymentWidget) {
                  handleEventInfoSubmit();
                } else {
                  setRenderTapPaymentWidget(true);
                  setIsModalOpen(true);
                }
              }
        }}
      />
    </div>
  );
};

const CheckoutActionButtonMobile = ({
  handleEventInfoSubmit,
  handleReqCallBackClick,
  hostEmail,
  hostMobile,
  hostName,
  isRequestCallBackApplicable,
  isValid,
  orderTotal,
  renderTapPaymentWidget,
  setLoading,
  setRenderTapPaymentWidget,
  setShowToast,
  setTapSanitizedToken
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // TODO: HP - try to apply class using DOM contex provider
  useEffect(() => {
    const addOrRemove = isOpen ? 'add' : 'remove';
    document.body.classList[`${addOrRemove}`]('overflow-hidden');
  }, [isOpen]);

  return (
    <div className='flex w-full justify-end'>
      <div className='fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-300 px-4 py-3 md:static md:p-0 md:border-none'>
        <ActionButton
          {...{
            isRequestCallBackApplicable,
            isValid,
            onClickActionButton: isRequestCallBackApplicable
              ? handleReqCallBackClick
              : () => {
                  if (!config.isRenderTapPaymentWidget) {
                    handleEventInfoSubmit();
                  } else {
                    setIsOpen(true);
                    setRenderTapPaymentWidget(true);
                  }
                }
          }}
        />
      </div>
      {isOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-70 pointer-events-none z-30' />
      )}
      {renderTapPaymentWidget && (
        <div className='fixed left-0 right-0 bottom-0 z-50 flex justify-center items-end bg-transparent'>
          <div className='w-full max-w-md bg-white rounded-t-lg p-4 shadow-xl'>
            <TapPaymentWidget
              {...{
                buttonBackgroundColorClass: 'bg-brand-gradient',
                hostEmail,
                hostMobile,
                hostName,
                onClickPayAmount: handleEventInfoSubmit,
                setIsOpen,
                setLoading,
                setRenderTapPaymentWidget,
                setShowToast,
                setTapSanitizedToken,
                showHeader: true,
                textColorClass: 'text-white',
                transactionAmountInUpperDenomination:
                  parseFormatPriceValueFromAPI(orderTotal)
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CheckoutActionButton = ({
  handleEventInfoSubmit,
  handleReqCallBackClick,
  hostEmail,
  hostMobile,
  hostName,
  isRequestCallBackApplicable,
  isValid,
  renderTapPaymentWidget,
  setLoading,
  setRenderTapPaymentWidget,
  setShowToast,
  setTapSanitizedToken
}) => {
  const { defaultHostCartDetail } = useUIContext();

  const { derivedValues: { orderTotal = 0 } = {} } =
    defaultHostCartDetail || {};

  const surface = GetUiDeviceSurface();
  const CheckoutActionButtonMap = {
    [DEVICE_SURFACE.MOBILE]: CheckoutActionButtonMobile,
    [DEVICE_SURFACE.WEB]: CheckoutActionButtonWeb
  };
  const CheckoutActionButtonComponent = CheckoutActionButtonMap[surface];

  return (
    <CheckoutActionButtonComponent
      {...{
        handleEventInfoSubmit,
        handleReqCallBackClick,
        hostEmail,
        hostMobile,
        hostName,
        isRequestCallBackApplicable,
        isValid,
        orderTotal,
        renderTapPaymentWidget,
        setLoading,
        setRenderTapPaymentWidget,
        setShowToast,
        setTapSanitizedToken
      }}
    />
  );
};

export default CheckoutActionButton;
